import nativeHelper from "@/lib/nativeHelper"
import { CapacitorHttp } from "@capacitor/core"
import { AxiosResponse, InternalAxiosRequestConfig } from "axios"

/**
 * Adds 'Origin' header to requests on iOS.
 * iOS doesn't automatically send the 'Origin' header,
 * but it's required by BE endpoints to apply mobile-specific
 * session durations.
 *
 * POST requests work fine — Capacitor's native bridge forwards
 * all headers through the native HTTP path.
 *
 */

const regexUrlsWithOriginHeader = [
  /^\/api\/v1\/auth\/login/,
  /^\/api\/v1\/auth\/refresh/,
  /^\/api\/v1\/accounts\/.*\/axle\/token/,
  /^\/api\/v1\/accounts\/.*\/axle\/onboard/,
  /\/api\/edge-config/,
]

/**
 * GET endpoints that need the Origin header require a workaround:
 * Capacitor on iOS routes GET requests through a WKWebView proxy that
 * silently strips 'Origin' (a forbidden header). POST requests are
 * unaffected because they go through Capacitor's native bridge directly.
 *
 * For matched GET requests we swap in a custom Axios adapter that calls
 * CapacitorHttp.request() directly, bypassing the broken proxy path.
 *
 * Currently only the axle token endpoint (GET) is affected.
 * If you add a new GET endpoint here, it will also need this adapter.
 * POST endpoints work without it.
 */
const regexGetUrlsRequiringNativeAdapter = [
  /^\/api\/v1\/accounts\/.*\/axle\/token/,
]

const nativeGetAdapter = async (
  config: InternalAxiosRequestConfig,
): Promise<AxiosResponse> => {
  const url = (config.baseURL || "") + (config.url || "")

  const headers: Record<string, string> = {}
  if (config.headers) {
    for (const [key, value] of Object.entries(
      config.headers.toJSON(true) as Record<string, unknown>,
    )) {
      if (typeof value === "string") headers[key] = value
    }
  }

  const response = await CapacitorHttp.request({
    url,
    method: (config.method || "GET").toUpperCase(),
    headers,
    data: config.data,
    params: config.params as Record<string, string> | undefined,
  })

  return {
    data: response.data,
    status: response.status,
    statusText: "",
    headers: response.headers,
    config,
  } as AxiosResponse
}

const setOriginInterceptor = async (request: InternalAxiosRequestConfig) => {
  if (!request.url) {
    return request
  }

  const requestUrl = request.url
  const requiresOriginHeader =
    nativeHelper.isIos() &&
    regexUrlsWithOriginHeader.some((regexUrl) => regexUrl.test(requestUrl))

  if (requiresOriginHeader) {
    request.headers = request.headers ?? {}
    request.headers.Origin =
      request.headers.Origin ?? globalThis.location.origin

    const isGet = !request.method || request.method.toLowerCase() === "get"
    const needsNativeAdapter =
      isGet &&
      regexGetUrlsRequiringNativeAdapter.some((r) => r.test(requestUrl))
    if (needsNativeAdapter) {
      request.adapter = nativeGetAdapter
    }
  }

  return request
}

export default setOriginInterceptor
